// Master Drop utilities: <md-scroll-top> and <md-popup>.

// On desktop Horizon scrolls inside .page-wrapper (html/body overflow hidden); on mobile the
// document scrolls. Read and drive whichever one is actually scrolling.
const scroller = () => {
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) {
    const overflow = getComputedStyle(wrapper).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return wrapper;
  }
  return document.scrollingElement || document.documentElement;
};

class MdScrollTop extends HTMLElement {
  connectedCallback() {
    this.offset = Number(this.dataset.offset) || 400;
    this.button = this.querySelector('button');
    this.queued = false;
    this.onScroll = () => {
      if (this.queued) return;
      this.queued = true;
      requestAnimationFrame(() => {
        this.queued = false;
        this.classList.toggle('is-visible', scroller().scrollTop > this.offset);
      });
    };
    this.onClick = () => {
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scroller().scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
      document.querySelector('#MainContent')?.focus?.({ preventScroll: true });
    };
    // Capture: scroll events of .page-wrapper do not bubble to window.
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    this.button?.addEventListener('click', this.onClick);
    this.onScroll();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    this.button?.removeEventListener('click', this.onClick);
  }
}

const storage = {
  get(key, session) {
    try {
      return (session ? window.sessionStorage : window.localStorage).getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value, session) {
    try {
      (session ? window.sessionStorage : window.localStorage).setItem(key, value);
    } catch {
      /* storage blocked: popup simply shows again next time */
    }
  },
};

class MdPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.key = this.dataset.key || 'md-popup';
    this.days = Number(this.dataset.days) || 0;
    this.succeeded = Boolean(this.querySelector('[data-success]'));

    this.onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target === this.dialog || target.closest('[data-close]')) this.close();
      const copy = target.closest('[data-copy]');
      if (copy) this.copy(copy);
    };
    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('close', () => this.remember());

    if (window.Shopify?.designMode) {
      this.onSelect = (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.open();
      };
      this.onDeselect = (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.dialog.close();
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
      return;
    }

    if (this.succeeded) {
      // Back from a successful sign-up: show the code once and never prompt again.
      storage.set(`${this.key}:subscribed`, '1');
      this.open();
      return;
    }
    if (this.dataset.enabled !== 'true' || !this.shouldShow()) return;
    this.timer = window.setTimeout(() => this.open(), (Number(this.dataset.delay) || 0) * 1000);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
    if (this.onDeselect) document.removeEventListener('shopify:section:deselect', this.onDeselect);
  }

  shouldShow() {
    if (storage.get(`${this.key}:subscribed`)) return false;
    if (this.days === 0) return !storage.get(`${this.key}:seen`, true);
    const last = Number(storage.get(`${this.key}:seen`)) || 0;
    return Date.now() - last > this.days * 86400000;
  }

  remember() {
    if (window.Shopify?.designMode) return;
    if (this.days === 0) storage.set(`${this.key}:seen`, '1', true);
    else storage.set(`${this.key}:seen`, String(Date.now()));
  }

  open() {
    if (this.dialog.open) return;
    // Never stack on top of another open modal (cart drawer, menu).
    if (!window.Shopify?.designMode && document.querySelector('dialog[open]')) return;
    this.dialog.showModal();
  }

  close() {
    this.dialog.close();
  }

  async copy(button) {
    const code = this.querySelector('[data-code]')?.textContent?.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      const label = button.textContent;
      button.textContent = button.dataset.copiedLabel || label;
      window.setTimeout(() => {
        button.textContent = label;
      }, 2000);
    } catch {
      const range = document.createRange();
      const node = this.querySelector('[data-code]');
      if (!node) return;
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }
}

if (!customElements.get('md-scroll-top')) customElements.define('md-scroll-top', MdScrollTop);
if (!customElements.get('md-popup')) customElements.define('md-popup', MdPopup);
